import bbox from '@turf/bbox';
import { index } from 'fp-ts/lib/Array';
import {
    fromNullable,
    fromPredicate,
    none,
    Option,
    some,
} from 'fp-ts/lib/Option';
import { Setoid, setoidNumber, setoidString } from 'fp-ts/lib/Setoid';
import { Coordinate } from 'ol/coordinate';
import { P } from '../components/elements';
import { iife } from '../lib/fn';
import { formatNumber, fromRecord, MessageKey, tr } from '../locale';
import {
    DirectGeometryObject,
    Feature,
    FeatureCollection,
    FieldsDescriptor,
    getFields,
    IServiceBaseLayer,
    MessageRecord,
    MultiPolygon,
    Properties,
} from '../source';
import { NonEmptyArray } from 'fp-ts/lib/NonEmptyArray2v';
import { Eq } from 'fp-ts/lib/Eq';
import { FeaturePath } from '../map/types';
import { scopeOption } from '../lib';

export interface Foldable {
    folded: boolean;
}

// export type NullOrUndefined = null | undefined;
export type Nullable<T> = T | null;
export type Undefined<T> = T | Nullable<T> | undefined;

export interface IdentifiedS {
    id: string;
}
export interface IdentifiedN {
    id: number;
}
export type Identified = IdentifiedS | IdentifiedN;

export interface Collection<T, _K extends string = string> {
    [k: string]: T;
}

// an equivalent of `concat`
export const updateCollection = <T>(
    coll: Collection<T>,
    key: string,
    value: T,
) => {
    coll[key] = value;
    return coll;
};

export const mapCollection = <T, R>(
    f: (a: string, b: T) => R,
    col: Collection<T>,
): R[] => Object.keys(col).map(key => f(key, col[key]));

export const mapCollectionC =
    <T, R>(f: (a: string, b: T) => R) =>
    (col: Collection<T>): R[] =>
        mapCollection(f, col);

export const getCollectionItem = <T>(
    key: string,
    col: Collection<T>,
): Option<T> => fromNullable(col[key]);

/**
 * A more granular Partial type.
 * Use case is POSTing a new object without an id
 */
export type PrecisePartial<T, PART extends keyof T> = {
    [K in PART]?: T[K];
} & Pick<T, Exclude<keyof T, PART>>;

export const asArray = <T>(a: T | T[]) => (Array.isArray(a) ? a : [a]);

export const sameArrays = <T>(a: T[], b: T[]) =>
    a.length === b.length && a.every((val, index) => val === b[index]);

export const asMutable = <T>(a: Readonly<T> | T) =>
    JSON.parse(JSON.stringify(a)) as T;

/** Arrays stored in local storage return as readonly arrays.
 * fp-ts primitives only accept mutable arrays.
 * This helper prevents us from the temptation of a type
 * assertion (as) and from putting concat everywhere.
 **/
export const toMutableArray = <T>(as: readonly T[]) => as.concat([]);

export const getNumber = (s?: string) => {
    if (s) {
        const n = parseFloat(s);
        if (!Number.isNaN(n)) {
            return some(n);
        }
    }
    return none;
};

// concat :: ([a],[a]) -> [a]
const concat = <A>(xs: A[], ys: A[]) => xs.concat(ys);

export const flatten = <T>(xs: T[][]) => xs.reduce(concat, []);
// export const flattenRecursive = <T>(
//     arr: T[] | T[][] | T[][][] | T[][][][],
//     result: T[] = []
// ) => {
//     arr.map(a => {
//         if (Array.isArray(a)) {
//             flattenRecursive(a, result);
//         } else {
//             result.push(a);
//         }
//     });
//     return result;
// };

export const swap = <T>(xs: T[], a: number, b: number) => {
    const temp = xs[a];
    xs[a] = xs[b];
    xs[b] = temp;
    return xs;
};

export const defaultCollection = <T>(): Collection<T> => ({});

export interface IMapBaseLayerTranslated {
    name: string;
    srs: string;
    params: {
        LAYERS: string;
        VERSION: string;
    };
    url: string;
}

export const hashMapBaseLayer = (name: MessageRecord, l: IServiceBaseLayer) => {
    return `${fromRecord(name)}|${l.url}|${fromRecord(l.params.LAYERS)}`;
};

export const setoidIdentified = <T extends Identified>(): Setoid<T> => ({
    equals: (a: Identified, b: Identified) => a.id === b.id,
});

// export const setoidTime: Setoid<Date> = {
//     equals: (a, b) => a.getTime() === b.getTime()
// };

export const setoidDate: Setoid<Date> = {
    equals: (a, b) =>
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate(),
};

export const uniq =
    <T>(o: Setoid<T>) =>
    (as: T[]): T[] =>
        as.reduce(
            (acc, a) => acc.filter(i => !o.equals(a, i)).concat([a]),
            [] as T[],
        );

export const uniqString = uniq(setoidString);
export const uniqEqual = <T>() => uniq<T>({ equals: (a, b) => a === b });
export const uniqNumber = uniq(setoidNumber);
export const uniqIdentified = <T extends Identified>(as: T[]) =>
    uniq(setoidIdentified<T>())(as);

export const arrayEquals = <T extends string | number | boolean>(
    a: T[],
    b: T[],
) =>
    a.length === b.length &&
    zip(a, b).reduce((acc, v) => acc && v[0] === v[1], true);

export const once = <ARG, RET>(f: (...args: ARG[]) => RET) => {
    type NotDone = {
        tag: 'no';
    };
    type Done = {
        tag: 'yes';
        mem: RET;
    };

    let done: NotDone | Done = { tag: 'no' };

    return (...args: ARG[]): RET => {
        switch (done.tag) {
            case 'no': {
                const mem = f(...args);
                done = {
                    tag: 'yes',
                    mem,
                };
                return mem;
            }
            case 'yes':
                return done.mem;
        }
    };
};

export const condOnce = (f: () => boolean) => {
    let done = false;

    return () => {
        if (!done) {
            done = f();
        }
    };
};

const memoKey = <ARGS extends unknown[]>(...args: ARGS) =>
    args.map(a => `${a}`).join('##');

export const memo = <Args extends unknown[], Ret>(
    f: (...args: Args) => Ret,
) => {
    const mem: Collection<Ret> = {};
    const inner = (...args: Args) => {
        const key = memoKey(...args);
        if (!(key in mem)) {
            mem[key] = f(...args);
        }
        return mem[key];
    };
    return inner;
};

// TODO: remove one of this two (nonEmpty vs notEmpty... )
export const nonEmpty = fromPredicate<unknown[]>(ls => ls.length > 0);
export const nonEmptyStr = fromPredicate<string>(s => s.length > 0);

export const empty = <T>(ns: T[] | readonly T[]): ns is NonEmptyArray<T> =>
    ns.length === 0;

export const notEmpty = <T>(ns: T[] | readonly T[]) =>
    ns.length > 0 ? some(ns) : none;

export const uniqIdGen = (prefix = '') => {
    let counter = 0;
    return () => {
        counter += 1;
        return `${prefix}${counter}`;
    };
};

export const uniqId = uniqIdGen('sdi-');

const APP_ID_KEY = '__app_id__';

export const addAppIdToFeature = (f: Feature) => {
    f.properties = {
        [APP_ID_KEY]: uniqId(),
        ...f.properties,
    };
};

const FEATURE_PROPS_BLACKLIST = new Set([APP_ID_KEY]);

export const getFeatureProperties = (f: Feature): Properties => {
    const props = f.properties;
    if (!props) {
        return null;
    }
    return Object.keys(props).reduce<Properties>((acc, k) => {
        if (FEATURE_PROPS_BLACKLIST.has(k)) {
            return acc;
        }
        return {
            [k]: props[k],
            ...acc,
        };
    }, {});
};

export const getFeaturePropOption = <T>(f: Feature, key: string): Option<T> => {
    const props = f.properties;
    if (!props) {
        return none;
    }
    return fromNullable(props[key]);
};

export const getLayerPropertiesKeys = (layer: FeatureCollection): string[] =>
    getFields(layer)
        .map(fields => fields.map(([name, _]) => name))
        .getOrElse(
            index(0, layer.features)
                .chain(feature => fromNullable(feature.properties))
                .map(Object.keys)
                .getOrElse([]),
        );

export const getLayerPropertiesKeysForNumbers = (
    layer: FeatureCollection,
): string[] =>
    getFields(layer)
        .map(fields =>
            fields.filter(([_, t]) => t === 'number').map(([name, _]) => name),
        )
        .getOrElse(
            index(0, layer.features)
                .chain(feature => fromNullable(feature.properties))
                .map(Object.keys)
                .getOrElse([]),
        );

export const getLayerFields = (layer: FeatureCollection) =>
    getFields(layer).alt(
        index(0, layer.features)
            .chain(feature => fromNullable(feature.properties))
            .map(props =>
                Object.keys(props).map<FieldsDescriptor>(key => {
                    const val = props[key];

                    switch (typeof val) {
                        case 'string':
                            return [key, 'string'];
                        case 'number':
                            return [key, 'number'];
                        case 'boolean':
                            return [key, 'boolean'];
                        default:
                            return [key, 'string'];
                    }
                }),
            ),
    );

// export const getLayerPropertiesKeysFiltered = (
//     fc: FeatureCollection,
//     pred: (a: any) => boolean
// ): string[] => {
//     if (fc.features.length === 0) {
//         return [];
//     }
//     const f = fc.features[0];
//     const props = f.properties;
//     if (!props) {
//         return [];
//     }
//     return Object.keys(props).reduce<string[]>((acc, k) => {
//         if (FEATURE_PROPS_BLACKLIST.has(k) || !pred(props[k])) {
//             return acc;
//         }
//         acc.push(k);
//         return acc;
//     }, []);
// };

// export const splitString = <T extends HTMLTagName>(
//     f: ElementFactory<T>,
//     sep = '\n'
// ) => (s: string) =>
//         s.split(sep).map((ss, index) => f({ key: `split-${index}` }, ss));

export const splitString = (s: string) =>
    s.split('\n').map((ss, index) => P({ key: `split-${index}` }, ss));

export const stringToParagraphs = splitString;

/*
 * implements binary search (recursive)
 *
 * https://en.wikipedia.org/wiki/Binary_search_algorithm
 * Where it's different from general implementation lies in the fact
 * that's the predicate which evaluates rather then numeric comparision.
 * Thus the predicate must know the key.
 *
 * @param min Number minimum value
 * @param max Number maximun value
 * @predicate Function(pivot) a function that evaluates the current mid value a la compareFunction
 * @context Object context to which the predicate is applied
 *
 */

export type BinaryPredicate = (a: number) => number;

export const binarySearch: (
    a: number,
    b: number,
    c: BinaryPredicate,
) => number = (min, max, predicate) => {
    const interval = max - min;
    const pivot = min + Math.floor(interval / 2);

    if (max === min) {
        return pivot;
    } else if (max < min) {
        // throw (new Error('MaxLowerThanMin'));
        return pivot;
    }

    if (predicate(pivot) > 0) {
        return binarySearch(min, pivot, predicate);
    } else if (predicate(pivot) < 0) {
        return binarySearch(pivot + 1, max, predicate);
    }
    return pivot;
};

/**
 *
 * At this point, I'm tired of of checking
 * both at all times when prod brakes.
 * Let's verbose!
 *
 */
export const isNotNullNorUndefined = <T>(x: Undefined<T>): x is T =>
    x !== undefined && x !== null;

export const filterNotNull = <T>(xs: Nullable<T>[]): T[] => {
    const r: T[] = [];
    for (let i = 0; i < xs.length; i += 1) {
        const v = xs[i];
        if (isNotNullNorUndefined(v)) {
            r.push(v);
        }
    }
    return r;
};

const withUnit =
    (k: MessageKey) =>
    (value: number, tf = 0) =>
        `${formatNumber(parseFloat(value.toFixed(tf)))}\u00A0${tr.core(k, {
            value,
        })}`;

export const withEuro = withUnit('unitEuro');
export const withEuroExclVAT = withUnit('unitEuroExclVAT');
export const withEuroInclVAT = withUnit('unitEuroInclVAT');
export const withEuroY = withUnit('unitEuroY');
export const withEuroY10 = withUnit('unitEuroY10');
export const withEuroY25 = withUnit('unitEuroY25');
export const withKWc = withUnit('unitKWc');
export const withTonsCO2 = withUnit('unitTonsCO2');
export const withTCO2 = withUnit('unitTCO2');
export const withTCO2Y = withUnit('unitTCO2Y');
export const withTCO2Y10 = withUnit('unitTCO2Y10');
export const withTCO2Y25 = withUnit('unitTCO2Y25');
export const withYear = withUnit('unitYear');
export const withMeter = withUnit('unitM');
export const withM2 = withUnit('unitM2');
export const withPercent = withUnit('unitPercent');
export const withKWh = withUnit('unitKWh');
export const withKWhY = withUnit('unitKWhY');
export const withLiter = withUnit('unitLiter');
export const withLiterDay = withUnit('unitLiterDay');

export const getPathElements = (p: string) =>
    p.split('/').filter(e => e.length > 0);

export const ensureArray = <T>(a: T | T[]): T[] => {
    if (Array.isArray(a)) {
        return a;
    }
    if (a === undefined) {
        return [];
    }
    return [a];
};

export const parseDate = (s: string) => {
    const d = Date.parse(s);
    return Number.isNaN(d) ? none : some(new Date(d));
};
export const datetime8601 = (d: Date) =>
    d.toISOString().slice(0, 16).replace('T', ' ');

export const date8601 = (d: Date) => datetime8601(d).slice(0, 10);

export const dateISOFormat = (d: Date) =>
    `${d.getFullYear()}-${fmtDateNb(d.getMonth() + 1)}-${fmtDateNb(
        d.getDate(),
    )}`; // this does NOT considerate timezone...

export const formatTime = (date: Date, sep = ':') => {
    const hours = padStart(date.getHours().toString(), 2, '0');
    const minutes = padStart(date.getMinutes().toString(), 2, '0');
    return `${hours}${sep}${minutes}`;
};

export const fmtDateNb = (n: number) => (n < 10 ? `0${n}` : `${n}`);
export const datetimeBEFormated = (d: Date): string => `${fmtDateNb(
    d.getDate(),
)}/${fmtDateNb(d.getMonth() + 1)}/${d.getFullYear()}
    ${fmtDateNb(d.getHours())}:${fmtDateNb(d.getMinutes())}`;

export const encodeUrlQuery = <D extends string | number | boolean>(
    o: Collection<D>,
) => mapCollection((k, v) => `${k}=${encodeURIComponent(v)}`, o).join('&');

export const getYYYYMMDD = (dt: Date, sep = '-') => {
    const year = padStart(dt.getFullYear().toString(10), 4, '0');
    const month = padStart((dt.getMonth() + 1).toString(10), 2, '0');
    const day = padStart(dt.getDate().toString(10), 2, '0');
    return [year, month, day].join(sep);
};

export const parseYYYYMMDD = (s: string, sep = '-') => {
    const parts = s.split(sep);
    return scopeOption()
        .let('year', index(0, parts).chain(tryNumber))
        .let(
            'month',
            index(1, parts)
                .chain(tryNumber)
                .map(m => m - 1),
        )
        .let('date', index(2, parts).chain(tryNumber));
};

export const getHHMM = (dt: Date, sep = ':') => {
    const hours = padStart(dt.getHours().toString(10), 2, '0');
    const minutes = padStart(dt.getMinutes().toString(10), 2, '0');
    return [hours, minutes].join(sep);
};

export const parseHHMM = (s: string, sep = ':') => {
    const parts = s.split(sep);
    return scopeOption()
        .let('hours', index(0, parts).chain(tryNumber))
        .let('minutes', index(1, parts).chain(tryNumber));
};

// we put it there as @turf/center doesn't come with types
export const center = (geojson: DirectGeometryObject): Coordinate => {
    const ext = bbox(geojson);
    const x = (ext[0] + ext[2]) / 2;
    const y = (ext[1] + ext[3]) / 2;
    return [x, y];
};

// from https://github.com/behnammodi/polyfill/blob/master/string.polyfill.js
export const padStart = (
    source: string,
    targetLength: number,
    padString = ' ',
) => {
    targetLength = targetLength >> 0; // floor if number or convert non-number to 0;
    padString = String(typeof padString !== 'undefined' ? padString : ' ');
    if (source.length > targetLength) {
        return String(source);
    } else {
        targetLength = targetLength - source.length;
        if (targetLength > padString.length) {
            padString += padString.repeat(targetLength / padString.length); // append to original to ensure we are longer than needed
        }
        return padString.slice(0, targetLength) + String(source);
    }
};

export const makeNumberList = (start: number, end: number) =>
    // tslint:disable-next-line: prefer-array-literal
    new Array(end + 1 - start).fill(0).map((_, index) => start + index);

export const tryNumber = (n: unknown) => {
    if (typeof n === 'number' && !Number.isNaN(n)) {
        return some(n);
    } else if (typeof n === 'string') {
        const pn = parseFloat(n);
        if (Number.isNaN(pn)) {
            return none;
        }
        return some(pn);
    }
    return none;
};

export const tryBoolean = (n: unknown) => {
    if (typeof n === 'boolean') {
        return some(n);
    }
    return none;
};

export const tryString = (n: unknown) => {
    if (typeof n === 'string') {
        return some(n);
    }
    return none;
};

export const tryDate = (val: unknown) => {
    if (val instanceof Date) {
        return some(val);
    } else if (typeof val === 'string') {
        return parseDate(val);
    } else if (typeof val === 'number') {
        return some(new Date(val));
    }
    return none;
};

export const tryFeaturePath = ({ layerId, featureId }: FeaturePath) =>
    layerId !== null && featureId !== null
        ? some({ layerId, featureId })
        : none;

export const tryTuple1 = <T>(a: T[]): Option<[T]> => {
    if (a.length === 1) {
        return some([a[0]]);
    } else {
        return none;
    }
};
export const tryTuple2 = <T>(a: T[]): Option<[T, T]> => {
    if (a.length === 2) {
        return some([a[0], a[1]]);
    } else {
        return none;
    }
};
export const tryTuple3 = <T>(a: T[]): Option<[T, T, T]> => {
    if (a.length === 3) {
        return some([a[0], a[1], a[2]]);
    } else {
        return none;
    }
};
export const tryTuple4 = <T>(a: T[]): Option<[T, T, T, T]> => {
    if (a.length === 4) {
        return some([a[0], a[1], a[2], a[3]]);
    } else {
        return none;
    }
};

export interface RangeMin {
    min: number;
}

export interface RangeMax {
    max: number;
}

// We postfix to avoid name collision
export type RangeT = RangeMax | RangeMin | (RangeMax & RangeMin);

export const range = (min: number, max: number): RangeMax & RangeMin => ({
    min,
    max,
});

export const rangeMin = (min: number): RangeMin => ({ min });

export const rangeMax = (max: number): RangeMax => ({ max });

export const tryStringRange = (r: RangeT) => {
    if ('min' in r && 'max' in r) {
        return fromPredicate<string>(
            s => s.length > r.min && s.length <= r.max,
        );
    } else if ('max' in r) {
        return fromPredicate<string>(s => s.length <= r.max);
    } else {
        return fromPredicate<string>(s => s.length > r.min);
    }
};

export const tryMultiPolygon = (
    geom: DirectGeometryObject,
): Option<MultiPolygon> => {
    switch (geom.type) {
        case 'MultiPolygon':
            return some(geom);
        case 'Polygon':
            return none; // TODO: try ay least
        default:
            return none;
    }
};

// too early
// export function* zipIterator<T0, T1>(a: T0[], b: T1[]): IterableIterator<[T0, T1]> {
//     const max = Math.min(a.length, b.length)
//     for (let i = 0; i < max; i += 1) {
//         yield [a[i], b[i]]
//     }
// }

export const zip = <T0, T1>(
    a: T0[] | Readonly<T0[]>,
    b: T1[] | Readonly<T1[]>,
): [T0, T1][] => {
    const max = Math.min(a.length, b.length);
    const result: [T0, T1][] = new Array(max);
    for (let i = 0; i < max; i += 1) {
        result[i] = [a[i], b[i]];
    }
    return result;
};

export const zip3 = <T0, T1, T2>(a: T0[], b: T1[], c: T2[]): [T0, T1, T2][] => {
    const max = Math.min(a.length, b.length, c.length);
    const result: [T0, T1, T2][] = new Array(max);
    for (let i = 0; i < max; i += 1) {
        result[i] = [a[i], b[i], c[i]];
    }
    return result;
};

export const min = (values: number[]) =>
    index(0, values).map(init =>
        values.reduce((acc, val) => (val < acc ? val : acc), init),
    );

export const max = (values: number[]) =>
    index(0, values).map(init =>
        values.reduce((acc, val) => (val > acc ? val : acc), init),
    );

export const minAndMax = (values: number[]) =>
    index(0, values).map(init =>
        values.reduce<[number, number]>(
            ([min, max], val) => [val < min ? val : min, val > max ? val : max],
            [init, init],
        ),
    );

export const dbg = <T>(val: T, name = '??') => {
    console.log(`dbg[${name}]>> `, val);
    return val;
};

export const getPercentile = (values: number[], percent: number) => {
    if (values.length > 0) {
        const k = (values.length - 1) * percent;
        const f = Math.floor(k);
        const c = Math.ceil(k);
        if (f == c) {
            return some(values[k]);
        }
        const d0 = values[f] * (c - k);
        const d1 = values[c] * (k - f);
        return some(d0 + d1);
    } else {
        return none;
    }
};

const absoluteURLRegex = new RegExp('^https?:');
/**
 * checkScheme ensures that URL has the same scheme than the location
 * fixes Mixed Content when getting URLs from a proxied server
 * @param url
 */
export const checkURLScheme = (url: string): string => {
    if (!absoluteURLRegex.test(url)) {
        return url; // browser will take care
    }
    const lp = window.location.protocol;
    if ('http:' === lp || url.slice(0, lp.length) === lp) {
        return url; // we're on the same page
    }

    // we're asking for http while we're on https
    return 'https' + url.slice(4);
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const noop = (..._args: unknown[]): void => void 0;

export const partitionArray = <T>(xs: T[], f: (x: T) => boolean) => {
    const left: T[] = [];
    const right: T[] = [];
    xs.forEach(x => (f(x) ? left.push(x) : right.push(x)));
    return [left, right];
};

export const toDataURL = (f: File) => {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result;
            if (typeof result === 'string') {
                resolve(result);
            } else {
                reject();
            }
        };
        reader.onerror = reject;
        reader.onabort = reject;
        reader.readAsDataURL(f);
    });
};

export const formatNumberDE = (n: number) =>
    new Intl.NumberFormat('de-DE', {}).format(n);

/**
 * see https://developer.mozilla.org/en-US/docs/Glossary/Base64#the_unicode_problem
 */
export const { base64Encode, base64Decode } = iife(() => {
    const decoder = new TextDecoder();
    const encoder = new TextEncoder();

    const base64Encode = (string: string) => {
        const bytes = encoder.encode(string);
        const binString = Array.from(bytes, byte =>
            String.fromCodePoint(byte),
        ).join('');
        return window.btoa(binString);
    };

    const base64Decode = (base64: string) => {
        const binString = window.atob(base64);
        const bytes = Uint8Array.from(binString, m => m.codePointAt(0) || 32);
        return decoder.decode(bytes);
    };

    return { base64Encode, base64Decode };
});

/**
 * Creates a new `Array` placing an element in between members of the input `Array`.
 *
 * @example
 * assert.deepStrictEqual(intersperse(9)([1, 2, 3, 4]), [1, 9, 2, 9, 3, 9, 4])
 */
export const intersperse =
    <T>(sep: T) =>
    (a: T[]) =>
        a.reduce((a, v) => [...a, v, sep], []).slice(0, -1);

export const eqIdentified: Eq<{ id: number | string }> = {
    equals: (a, b) => a.id === b.id,
};

const compareId = (a: number | string, b: number | string) =>
    typeof a === 'string' && typeof b === 'string'
        ? a.localeCompare(b)
        : typeof a === 'string'
          ? -1
          : typeof b === 'string'
            ? 1
            : a - b;

export const compFeaturePath = (a: FeaturePath, b: FeaturePath) =>
    tryFeaturePath(a)
        .chain(a =>
            tryFeaturePath(b).map(b => {
                const layerComp = compareId(a.layerId, b.layerId);
                return layerComp === 0
                    ? compareId(a.featureId, b.featureId)
                    : layerComp;
            }),
        )
        .getOrElse(0);

export const eqFeaturePath = (a: FeaturePath, b: FeaturePath) =>
    a.layerId === b.layerId && a.featureId === b.featureId;

export const eqFeaturePathList = (a: FeaturePath[], b: FeaturePath[]) => {
    if (a.length === b.length) {
        return zip(a.sort(compFeaturePath), b.sort(compFeaturePath)).every(
            ([a, b]) => eqFeaturePath(a, b),
        );
    }
    return false;
};

const replaceIfIdenticalAttribute =
    <T>(key: keyof T, new_object: T) =>
    (old_object: T) =>
        old_object[key] === new_object[key] ? new_object : old_object;

export const replaceWhenIdenticalAttribute = <T>(
    attribute_key: keyof T,
    new_value: T,
    array: T[],
) => array.map(replaceIfIdenticalAttribute(attribute_key, new_value));

export const swapItems = <T>(
    list: T[],
    firstIndex: number,
    secondIndex: number,
) =>
    list.map((item, index) => {
        if (index === firstIndex) return list[secondIndex];
        if (index === secondIndex) return list[firstIndex];
        return item;
    });

/**
 * A function that gives a value to a handler and returns the original value unchanged. Useful for Promise pipelines.
 *
 * Here's an example that takes a Promise<Form> load the referentials for the form and then pass the original form to the function assignForm.
 *
 * ```ts
 * fetchFormForSharedPreview(token).then(tap(loadReferentials)).then(assignForm)
 * ```
 */
export const tap =
    <T>(handler: (val: T) => void) =>
    (value: T) => {
        handler(value);
        return value;
    };
